import { z } from "zod";

import { machineTypeSchema } from "../machine-type";
import { machineWithCorrugateTracksWithOffsetSchema } from "../machine-with-corrugate-tracks";

import { emLongHeadSchema, emPhysicalMachineSettingsSchema } from "./em-machine-request";

const cartonReleaseBehaviorSchema = z.enum(["Automatic", "ReleaseCarton", "RequestCarton"]);
const emMachinePhysicalMachineSettingsSchema = emPhysicalMachineSettingsSchema.extend({
	longHead: emLongHeadSchema.extend({
		minimumPosition: z.number(),
		maximumPosition: z.number(),
	}),
});

export const emMachineSchema = machineWithCorrugateTracksWithOffsetSchema.extend({
	machineType: z.literal(machineTypeSchema.enum.Em),
	crossConveyorConnected: z.boolean(),
	physicalMachineSettings: emMachinePhysicalMachineSettingsSchema,
	releaseBehavior: cartonReleaseBehaviorSchema,
	releaseCartonWithFootPedal: z.boolean().optional(),
});

// TODO @eb remove this function when the EM aligns with our decided upon schemas
export const prepareEmMachineData = (data: unknown): unknown => {
	if (typeof data !== "object" || data === null) {
		return data;
	}

	const input = data as Record<string, unknown>;

	const iotDevice = {
		hubName: input.iotHubName ?? "",
		deviceId: input.iotEdgeDeviceId ?? "",
		moduleId: input.iotEdgeModuleId ?? "",
		moduleVersion: input.edgeModuleVersion ?? "",
		lastProofOfLife: input.lastProofOfLifeTimeStamp,
	};

	const physicalMachineSettings = input.physicalMachineSettings;

	let tracks = input.tracks;
	if (Array.isArray(tracks)) {
		tracks = tracks.map((t) => {
			if (typeof t !== "object" || t === null) {
				return t;
			}

			const track = t as Record<string, unknown>;

			let assignedMaterial = track.assignedMaterial;
			if (typeof assignedMaterial === "object" && assignedMaterial !== null) {
				const material = assignedMaterial as Record<string, unknown>;

				assignedMaterial = {
					...material,
					brand: material.quality?.toString() ?? "",
				};
			}

			return {
				...track,
				isEmpty: track.isTrackEmpty ?? false,
				assignedMaterial,
			};
		});
	}

	const error = input.error as Record<string, unknown>;
	let stopReason: {
		stopCode: number;
		message: string;
		arguments: number[];
		isError: boolean;
	} = { stopCode: 0, message: "", arguments: [], isError: false };
	if (typeof error === "object" && error !== null) {
		stopReason = {
			stopCode: (error.code as number) ?? 0,
			message: (error.message as string) ?? "",
			arguments: Array.isArray(error.arguments) ? error.arguments.map(Number) : [],
			isError: error.code !== 0,
		};
	}

	let workstations = input.workstations;
	if (!Array.isArray(workstations) || workstations.length < 1) {
		workstations = [
			{
				number: 1,
				alarms:
					stopReason.stopCode > 0
						? [{ code: stopReason.stopCode, message: stopReason.message, arguments: stopReason.arguments.map(String) }]
						: [],
				warnings: [],
				stopReason: {
					code: stopReason.stopCode,
					message: stopReason.message,
					arguments: stopReason.arguments.map(String),
					isError: stopReason.isError,
				},
			},
		];
	}

	return {
		...input,
		machineType: "Em",
		physicalMachineSettings,
		iotDevice,
		stopReason,
		activeWarnings: [],
		tracks,
		workstations,
	};
};

export type EmMachine = z.infer<typeof emMachineSchema>;
